import { useState } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table'
import { Button } from '@/shared/components/ui/button'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Eye, ArrowUp, FileBarChart, FilePlus, Pencil, Copy, Layout, FileText, Send, Calendar, Mail, MessageSquare, MoreVertical, Megaphone, ExternalLink, CheckCircle2, Clock, Trophy, Loader2 } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { EstadosEditDialog } from './EstadosEditDialog'
import type { Campania, Categorizacion } from '@/modules/multichannel/types/campania'

// Tipos de campaña que incluyen cada canal
const WHATSAPP_TIPOS = ['Whatsapp', 'Email + Whatsapp']
const EMAIL_TIPOS = ['Email', 'Email + Whatsapp']

function getCampaignChannels(tipo: string): { hasEmail: boolean; hasWhatsApp: boolean } {
  return {
    hasEmail: EMAIL_TIPOS.includes(tipo),
    hasWhatsApp: WHATSAPP_TIPOS.includes(tipo),
  }
}

interface CampaniasTableProps {
  campanias: Campania[]
  isLoading: boolean
  page: number
  totalPages: number
  pageSize?: number
  onPageChange: (page: number) => void
  onPageSizeChange?: (pageSize: number) => void
  onViewDetails?: (id: number) => void
  onViewReport?: (campaniaId: number) => void
  onCreateReport?: (campaniaId: number) => void
  onCreateWhatsAppReport?: (campaniaId: number) => void
  onEditReport?: (campaniaId: number) => void
  onEditWhatsAppReport?: (campaniaId: number) => void
  onDuplicate?: (id: number) => void
  onPreviewHtml?: (campaniaId: number, nombre: string) => void
  onPreviewWhatsApp?: (campaniaId: number, nombre: string) => void
  canManage?: boolean
  categorizaciones?: Record<string, Categorizacion>
}

export function CampaniasTable({
  campanias,
  isLoading,
  page,
  totalPages,
  pageSize = 25,
  onPageChange,
  onPageSizeChange,
  onViewDetails,
  onViewReport,
  onCreateReport,
  onCreateWhatsAppReport,
  onEditReport,
  onEditWhatsAppReport,
  onDuplicate,
  onPreviewHtml,
  onPreviewWhatsApp,
  canManage = false,
  categorizaciones,
}: CampaniasTableProps) {
  const [editingCampania, setEditingCampania] = useState<Campania | null>(null)

  const getCalificacionColor = (calificacion: string) => {
    switch (calificacion) {
      case 'A+': return 'bg-emerald-500 text-white'
      case 'A': return 'bg-blue-500 text-white'
      case 'B': return 'bg-yellow-500 text-white'
      case 'C': return 'bg-rose-500 text-white'
      default: return 'bg-gray-400 text-white'
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-'
    // Parsear sin conversión de zona horaria (formato YYYY-MM-DD)
    const [year, month, day] = dateString.split('-').map(Number)
    const date = new Date(year, month - 1, day)
    return new Intl.DateTimeFormat('es-EC', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date)
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(10)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    )
  }

  if (campanias.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-lg font-medium text-muted-foreground">
          No se encontraron campañas
        </p>
        <p className="text-sm text-muted-foreground">
          Intenta ajustar los filtros o crea una nueva campaña
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-card overflow-hidden">
        <Table>
          <TableHeader className="bg-gray-100 dark:bg-[hsl(220,15%,16%)] [&_tr]:hover:bg-gray-100 dark:[&_tr]:hover:bg-[hsl(220,15%,16%)]">
            <TableRow>
              <TableHead>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Fecha
                  <ArrowUp className="h-3 w-3 text-gray-600" />
                </div>
              </TableHead>
              <TableHead><div className="flex items-center gap-2"><Megaphone className="h-4 w-4" />Laboratorio</div></TableHead>
              <TableHead><div className="flex items-center gap-2"><FileText className="h-4 w-4" />Campaña</div></TableHead>
              <TableHead><div className="flex items-center gap-2"><Layout className="h-4 w-4" />Formato</div></TableHead>
              <TableHead><div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4" />Estados</div></TableHead>
              <TableHead className="text-center"><div className="flex items-center justify-center gap-2"><Trophy className="h-4 w-4" />Score</div></TableHead>
              <TableHead className="text-center"><div className="flex items-center justify-center gap-2"><FileBarChart className="h-4 w-4" />Reporte</div></TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {campanias.map((campania) => (
              <TableRow key={campania.id} className="hover:bg-muted/50">
                <TableCell>
                  <div className="text-sm">
                    <div>{formatDate(campania.fecha_publicacion)}</div>
                    {campania.hora && (
                      <div className="text-xs text-muted-foreground">{campania.hora.slice(0, 5)}</div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {(() => {
                    const labName = campania.marca_principal_nombre || campania.marca
                    const colorNombre = campania.marca_color_nombre
                    const colorTono = campania.marca_color_tono || '500'
                    return (
                      <div className="flex items-center gap-1.5">
                        <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${
                          colorNombre ? `bg-${colorNombre}-${colorTono}` : 'bg-gray-400'
                        }`} />
                        <span className="text-sm">{labName}</span>
                      </div>
                    )
                  })()}
                </TableCell>
                <TableCell className="font-medium">
                  <div className="max-w-[220px] truncate">
                    {campania.nombre_campania}
                  </div>
                  <div className="text-[11px] text-muted-foreground font-normal">{campania.tipo_envio}</div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    {campania.tiene_html ? (
                      <button
                        className="inline-flex items-center gap-1 text-sm text-blue-800 hover:underline dark:text-blue-300"
                        onClick={() => onPreviewHtml?.(campania.id, campania.nombre_campania)}
                      >
                        <Mail className="h-3 w-3" />
                        {campania.tiene_wa_contenido ? 'Html' : campania.formato}
                        <ExternalLink className="h-3 w-3" />
                      </button>
                    ) : !campania.tiene_wa_contenido ? (
                      <span className="text-sm text-muted-foreground">{campania.formato}</span>
                    ) : null}
                    {campania.tiene_wa_contenido && (
                      <button
                        className="inline-flex items-center gap-1 text-sm text-green-700 hover:underline dark:text-green-400"
                        onClick={() => onPreviewWhatsApp?.(campania.id, campania.nombre_campania)}
                      >
                        <MessageSquare className="h-3 w-3" />
                        WhatsApp
                        <ExternalLink className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs text-muted-foreground">
                      Contenido: <span className={campania.estado_contenido.toLowerCase().includes('finalizado') ? 'text-emerald-600 dark:text-emerald-400' : ''}>{campania.estado_contenido}</span>
                    </span>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1" title={`Envío: ${campania.estado_envio}`}>
                        <Send className="h-3.5 w-3.5 text-muted-foreground" />
                        {campania.estado_envio.toLowerCase().includes('enviado') ? (
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                        ) : (
                          <Clock className="h-3.5 w-3.5 text-gray-400" />
                        )}
                      </div>
                      <div className="flex items-center gap-1" title={`Reporte: ${campania.estado_reporte}`}>
                        <FileBarChart className="h-3.5 w-3.5 text-muted-foreground" />
                        {campania.estado_reporte.toLowerCase().includes('generado') ? (
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                        ) : (
                          <Clock className="h-3.5 w-3.5 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  {categorizaciones?.[String(campania.id)] ? (
                    <span className={`inline-flex items-center justify-center h-5 w-5 rounded-full text-[10px] font-bold ${getCalificacionColor(categorizaciones[String(campania.id)].calificacion)}`}>
                      {categorizaciones[String(campania.id)].calificacion}
                    </span>
                  ) : (
                    <Loader2 className="h-3.5 w-3.5 text-gray-400 mx-auto animate-spin" />
                  )}
                </TableCell>
                <TableCell className="text-center">
                  {canManage ? (
                    campania.estado_reporte.toLowerCase().includes('generado') ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onViewReport?.(campania.id)}
                        title="Ver Reporte"
                      >
                        <FileBarChart className="h-4 w-4 text-gray-600" />
                      </Button>
                    ) : (
                      (() => {
                        const { hasEmail, hasWhatsApp } = getCampaignChannels(campania.tipo || '')
                        const isCombined = hasEmail && hasWhatsApp

                        if (isCombined) {
                          return (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  title="Crear Reporte"
                                >
                                  <FilePlus className="h-4 w-4 text-gray-600" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => onCreateReport?.(campania.id)}>
                                  <Mail className="mr-2 h-4 w-4" />
                                  Reporte Email
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => onCreateWhatsAppReport?.(campania.id)}>
                                  <MessageSquare className="mr-2 h-4 w-4 text-green-600" />
                                  Reporte WhatsApp
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )
                        }

                        if (hasWhatsApp) {
                          return (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onCreateWhatsAppReport?.(campania.id)}
                              title="Crear Reporte WhatsApp"
                            >
                              <FilePlus className="h-4 w-4 text-green-600" />
                            </Button>
                          )
                        }

                        return (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onCreateReport?.(campania.id)}
                            title="Crear Reporte"
                          >
                            <FilePlus className="h-4 w-4 text-gray-600" />
                          </Button>
                        )
                      })()
                    )
                  ) : (
                    campania.estado_reporte.toLowerCase().includes('generado') && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onViewReport?.(campania.id)}
                        title="Ver Reporte"
                      >
                        <FileBarChart className="h-4 w-4 text-gray-600" />
                      </Button>
                    )
                  )}
                </TableCell>
                <TableCell>
                  {canManage && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onViewDetails?.(campania.id)}>
                          <Eye className="mr-2 h-4 w-4" />
                          Ver detalles
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setEditingCampania(campania)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Editar estados
                        </DropdownMenuItem>
                        {campania.estado_reporte.toLowerCase().includes('generado') && (() => {
                          const { hasEmail, hasWhatsApp } = getCampaignChannels(campania.tipo || '')
                          return (
                            <>
                              {hasEmail && (
                                <DropdownMenuItem onClick={() => onEditReport?.(campania.id)}>
                                  <Mail className="mr-2 h-4 w-4" />
                                  Editar reporte email
                                </DropdownMenuItem>
                              )}
                              {hasWhatsApp && (
                                <DropdownMenuItem onClick={() => onEditWhatsAppReport?.(campania.id)}>
                                  <MessageSquare className="mr-2 h-4 w-4 text-green-600" />
                                  Editar reporte WhatsApp
                                </DropdownMenuItem>
                              )}
                              {!hasEmail && !hasWhatsApp && (
                                <DropdownMenuItem onClick={() => onEditReport?.(campania.id)}>
                                  <FileBarChart className="mr-2 h-4 w-4" />
                                  Editar reporte
                                </DropdownMenuItem>
                              )}
                            </>
                          )
                        })()}
                        <DropdownMenuItem onClick={() => onDuplicate?.(campania.id)}>
                          <Copy className="mr-2 h-4 w-4" />
                          Duplicar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Paginación */}
      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Mostrar</span>
          <Select
            value={pageSize.toString()}
            onValueChange={(value) => onPageSizeChange?.(parseInt(value))}
          >
            <SelectTrigger className="w-[80px] h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground">por página</span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(1)}
            disabled={page === 1}
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(page - 1)}
            disabled={page === 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground min-w-[100px] text-center">
            {page} de {totalPages}
          </span>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(page + 1)}
            disabled={page === totalPages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(totalPages)}
            disabled={page === totalPages}
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Diálogo de edición de estados */}
      {editingCampania && (
        <EstadosEditDialog
          campania={editingCampania}
          open={!!editingCampania}
          onOpenChange={(open) => !open && setEditingCampania(null)}
        />
      )}
    </div>
  )
}
